import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  getTeacherRoster,
  getAttendance,
  saveAttendance,
  getAbsenceReasons,
} from '../../services/teacher';
import {
  normalizeHhmm,
  nowRoundedToFiveMinutes,
  recordToUiStatus,
} from '../../utils/attendanceDraft';

const todayStr = () => new Date().toISOString().slice(0, 10);

export default function AttendanceDayPage() {
  const [date, setDate] = useState(todayStr);
  const [roster, setRoster] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [byStudent, setByStudent] = useState({});
  const [local, setLocal] = useState({});
  const [dirty, setDirty] = useState({});
  const [notesOpen, setNotesOpen] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const loadReasons = useCallback(() => {
    getAbsenceReasons()
      .then((res) => setReasons(res.data?.results || res.data || []))
      .catch(() => setReasons([]));
  }, []);

  useEffect(() => {
    loadReasons();
  }, [loadReasons]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([getTeacherRoster(), getAttendance(date)])
      .then(([rosterRes, attRes]) => {
        if (cancelled) return;
        const students = rosterRes.data?.results || rosterRes.data || [];
        setRoster(students);
        const records = attRes.data?.results || attRes.data || [];
        const map = {};
        records.forEach((r) => {
          if (r && r.student != null) {
            map[r.student] = r;
          }
        });
        setByStudent(map);
        setLocal({});
        setDirty({});
        setError(null);
      })
      .catch((e) => {
        if (!cancelled) setError(e.response?.data?.detail || e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [date]);

  const getUiStatus = (studentId) => {
    if (local[studentId] !== undefined) return local[studentId];
    return recordToUiStatus(byStudent[studentId]);
  };

  const getUiReason = (studentId) => {
    const key = `reason_${studentId}`;
    if (local[key] !== undefined) return local[key];
    const base = byStudent[studentId]?.absence_reason;
    if (base) return base;
    return (
      reasons.find((r) => r.reason_code === 'UNKNOWN')?.reason_code
      || reasons[0]?.reason_code
      || ''
    );
  };

  const getUiLateTime = (studentId) => {
    const key = `late_${studentId}`;
    if (local[key] !== undefined) return local[key];
    return normalizeHhmm(byStudent[studentId]?.late_arrival_time);
  };

  const getUiEarlyTime = (studentId) => {
    const key = `early_${studentId}`;
    if (local[key] !== undefined) return local[key];
    return normalizeHhmm(byStudent[studentId]?.early_dismissal_time);
  };

  const getUiNotes = (studentId) => {
    const key = `notes_${studentId}`;
    if (local[key] !== undefined) return local[key];
    return byStudent[studentId]?.notes || '';
  };

  const markDirty = (studentId, patch) => {
    setLocal((prev) => ({ ...prev, ...patch }));
    setDirty((prev) => ({ ...prev, [studentId]: true }));
  };

  const setStudentStatus = (studentId, status) => {
    const patch = { [studentId]: status };
    if (status === 'absent' || status === '') {
      patch[`late_${studentId}`] = '';
      patch[`early_${studentId}`] = '';
    }
    markDirty(studentId, patch);
  };

  const setStudentReason = (studentId, reasonCode) => {
    markDirty(studentId, { [`reason_${studentId}`]: reasonCode });
  };

  const setLateChecked = (studentId, checked) => {
    markDirty(studentId, {
      [`late_${studentId}`]: checked ? (getUiLateTime(studentId) || nowRoundedToFiveMinutes()) : '',
    });
  };

  const setEarlyChecked = (studentId, checked) => {
    markDirty(studentId, {
      [`early_${studentId}`]: checked ? (getUiEarlyTime(studentId) || nowRoundedToFiveMinutes()) : '',
    });
  };

  const setLateTime = (studentId, value) => {
    markDirty(studentId, { [`late_${studentId}`]: value || '' });
  };

  const setEarlyTime = (studentId, value) => {
    markDirty(studentId, { [`early_${studentId}`]: value || '' });
  };

  const setStudentNotes = (studentId, value) => {
    markDirty(studentId, { [`notes_${studentId}`]: value });
  };

  const hasDirty = Object.keys(dirty).length > 0;

  const shiftDate = (delta) => {
    if (hasDirty && !window.confirm('You have unsaved marks. Switch date and discard them?')) {
      return;
    }
    const d = new Date(`${date}T12:00:00`);
    d.setDate(d.getDate() + delta);
    setDate(d.toISOString().slice(0, 10));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    const records = [];
    Object.keys(dirty).forEach((sid) => {
      const id = Number(sid);
      let st = getUiStatus(id);
      let reason = getUiReason(id);
      if (st === 'absent' && !reason) {
        reason = reasons.find((r) => r.reason_code === 'UNKNOWN')?.reason_code || reasons[0]?.reason_code;
      }
      records.push({
        student_id: id,
        status: st,
        absence_reason: st === 'absent' ? reason : null,
        late_arrival_time: st === 'present' ? (getUiLateTime(id) || null) : null,
        early_dismissal_time: st === 'present' ? (getUiEarlyTime(id) || null) : null,
        notes: getUiNotes(id) || '',
      });
    });
    try {
      await saveAttendance({ date, records });
      setSuccess('Attendance saved.');
      setDirty({});
      const attRes = await getAttendance(date);
      const list = attRes.data?.results || attRes.data || [];
      const map = {};
      list.forEach((r) => {
        if (r && r.student != null) map[r.student] = r;
      });
      setByStudent(map);
      setLocal({});
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const rosterSorted = useMemo(
    () => [...roster].sort((a, b) => {
      const ln = (a.last_name || '').localeCompare(b.last_name || '', undefined, { sensitivity: 'base' });
      if (ln !== 0) return ln;
      return (a.first_name || '').localeCompare(b.first_name || '', undefined, { sensitivity: 'base' });
    }),
    [roster],
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!roster.length) {
    return (
      <Alert severity="info">No students assigned to your classroom.</Alert>
    );
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <IconButton onClick={() => shiftDate(-1)} aria-label="Previous day" size="large" sx={{ minWidth: 48, minHeight: 48 }}>
          <ChevronLeftIcon />
        </IconButton>
        <Box sx={{ textAlign: 'center', flex: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {new Date(`${date}T12:00:00`).toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </Typography>
          <input
            type="date"
            value={date}
            onChange={(e) => {
              if (hasDirty && !window.confirm('You have unsaved marks. Switch date and discard them?')) return;
              setDate(e.target.value);
            }}
            style={{ marginTop: 8, fontSize: 16, padding: 8, maxWidth: '100%' }}
          />
        </Box>
        <IconButton onClick={() => shiftDate(1)} aria-label="Next day" size="large" sx={{ minWidth: 48, minHeight: 48 }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {rosterSorted.map((s) => {
          const st = getUiStatus(s.id);
          const reason = getUiReason(s.id);
          const lateTime = getUiLateTime(s.id);
          const earlyTime = getUiEarlyTime(s.id);
          const notes = getUiNotes(s.id);
          // Collapsed by default; ● indicates an existing note without forcing open.
          const notesExpanded = !!notesOpen[s.id];
          const isDirty = !!dirty[s.id];
          return (
            <PaperLike key={s.id} dirty={isDirty}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body1" sx={{ fontWeight: 500, minWidth: 0, flex: 1, fontSize: '1.1rem' }}>
                  {s.first_name} {s.last_name}
                </Typography>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id={`status-${s.id}`}>Status</InputLabel>
                  <Select
                    labelId={`status-${s.id}`}
                    label="Status"
                    value={st || ''}
                    onChange={(e) => setStudentStatus(s.id, e.target.value)}
                    sx={{ minHeight: 44, fontWeight: 600 }}
                  >
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="present">Present</MenuItem>
                    <MenuItem value="absent">Absent</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {st === 'absent' && (
                <FormControl fullWidth size="small" sx={{ mt: 1.5 }}>
                  <InputLabel id={`reason-${s.id}`}>Reason</InputLabel>
                  <Select
                    labelId={`reason-${s.id}`}
                    label="Reason"
                    value={reason || ''}
                    onChange={(e) => setStudentReason(s.id, e.target.value)}
                    required
                    sx={{ minHeight: 44 }}
                  >
                    {reasons.map((r) => (
                      <MenuItem key={r.reason_code} value={r.reason_code}>
                        {r.reason_label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {st === 'present' && (
                <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={Boolean(lateTime)}
                          onChange={(e) => setLateChecked(s.id, e.target.checked)}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                        />
                      )}
                      label="Late arrival"
                    />
                    {lateTime ? (
                      <TextField
                        type="time"
                        size="small"
                        value={lateTime}
                        onChange={(e) => setLateTime(s.id, e.target.value)}
                        inputProps={{ step: 300 }}
                        sx={{ width: 130 }}
                      />
                    ) : null}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <FormControlLabel
                      control={(
                        <Checkbox
                          checked={Boolean(earlyTime)}
                          onChange={(e) => setEarlyChecked(s.id, e.target.checked)}
                          sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
                        />
                      )}
                      label="Early dismissal"
                    />
                    {earlyTime ? (
                      <TextField
                        type="time"
                        size="small"
                        value={earlyTime}
                        onChange={(e) => setEarlyTime(s.id, e.target.value)}
                        inputProps={{ step: 300 }}
                        sx={{ width: 130 }}
                      />
                    ) : null}
                  </Box>
                </Box>
              )}
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  onClick={() => setNotesOpen((prev) => ({ ...prev, [s.id]: !notesExpanded }))}
                  endIcon={notesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ minHeight: 40, textTransform: 'none' }}
                >
                  Notes{notes ? ' ●' : ''}
                </Button>
                <Collapse in={notesExpanded}>
                  <TextField
                    fullWidth
                    size="small"
                    multiline
                    minRows={2}
                    placeholder="Notes for this child"
                    value={notes}
                    onChange={(e) => setStudentNotes(s.id, e.target.value)}
                    sx={{ mt: 0.5 }}
                  />
                </Collapse>
              </Box>
            </PaperLike>
          );
        })}
      </Box>

      <Box
        sx={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: { xs: 56, md: 0 },
          p: 2,
          bgcolor: 'background.paper',
          borderTop: 1,
          borderColor: 'divider',
          zIndex: 9,
        }}
      >
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={!hasDirty || saving}
          onClick={handleSave}
          sx={{ minHeight: 48 }}
        >
          {saving ? 'Saving…' : 'Save attendance'}
        </Button>
      </Box>
    </Box>
  );
}

function PaperLike({ children, dirty }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: dirty ? 'primary.main' : 'divider',
        borderLeftWidth: dirty ? 4 : 1,
        boxShadow: 1,
      }}
    >
      {children}
    </Box>
  );
}
